import { type ReactNode } from 'react';

import { useHandshake } from '@/handshake/useHandshake.js';

import { HandshakeProvider } from './HandshakeContext.js';
import { TenantTheme } from './TenantTheme.js';

/**
 * Nothing renders until the host has handed over a token. Both route trees sit
 * behind this, so no component below has to consider a null JWT.
 */
export function HandshakeGate({ children }: { children: ReactNode }) {
  const handshake = useHandshake();

  if (handshake.status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md rounded-brand border border-red-200 bg-red-50 p-6 text-center">
          <h1 className="text-base font-semibold text-red-900">Could not connect to Kadanza</h1>
          <p className="mt-2 text-sm text-red-700">{handshake.error}</p>
          <p className="mt-4 text-xs text-red-600">
            This page has to be opened from inside Kadanza so the host can pass its tenant context.
          </p>
        </div>
      </div>
    );
  }

  if (handshake.status === 'connecting') {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <p className="animate-pulse text-sm text-slate-500">Connecting to Kadanza…</p>
      </div>
    );
  }

  return (
    <HandshakeProvider value={handshake}>
      <TenantTheme tokens={handshake.designTokens} />
      {children}
    </HandshakeProvider>
  );
}
